import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { API_BASE_URL as API } from '@/lib/apiBase';

export function OTPChallengeCaptcha({ onVerify }) {
  const [id] = useState(() => `otp-${Math.random().toString(36).slice(2)}`);
  useEffect(() => {
    const key = process.env.REACT_APP_TURNSTILE_SITE_KEY;
    if (!key) return undefined;
    let widget;
    const timer = setInterval(() => {
      if (window.turnstile && widget === undefined) {
        widget = window.turnstile.render(`#${id}`, { sitekey: key, callback: onVerify,
          'expired-callback': () => onVerify(''), 'error-callback': () => onVerify('') });
      }
    }, 200);
    if (!document.querySelector('script[data-turnstile-script]')) {
      const script = document.createElement('script');
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
      script.dataset.turnstileScript = 'true'; script.async = true; document.body.appendChild(script);
    }
    return () => { clearInterval(timer); if (widget !== undefined) window.turnstile?.remove(widget); };
  }, [id, onVerify]);
  return <div id={id} />;
}

export const authDestination = { artist: '/my-account', collaborator: '/collaborator/account', customer: '/marketplace/account' };

export default function OTPEntry({ onProof, accountType, onLinked }) {
  const [mobile, setMobile] = useState('');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [channel, setChannel] = useState('');
  const [proof, setProof] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [captcha, setCaptcha] = useState('');
  const [captchaKey, setCaptchaKey] = useState(0);
  const [method, setMethod] = useState(accountType ? 'mobile' : 'email');
  const onCaptcha = useCallback(value => setCaptcha(value), []);
  useEffect(() => {
    if (!cooldown) return undefined;
    const timer = setTimeout(() => setCooldown(value => value - 1), 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);
  const scope = accountType ? { purpose: 'link', account_type: accountType } : {};
  const send = async () => {
    setBusy(true); setError('');
    try {
      const { data } = await axios.post(`${API}/otp/${method === 'email' ? 'email/' : ''}send`, method === 'email'
        ? { email, turnstile_token: captcha }
        : { mobile, email, turnstile_token: captcha, ...scope }, { withCredentials: true });
      setChannel(data.channel); setCooldown(30); setCode('');
    } catch (err) { setError(typeof err.response?.data?.detail === 'string' ? err.response.data.detail : 'Unable to send a code. Please try again.'); }
    finally { setBusy(false); setCaptcha(''); setCaptchaKey(value => value + 1); }
  };
  const verify = async () => {
    setBusy(true); setError('');
    try {
      const { data } = await axios.post(`${API}/otp/${method === 'email' ? 'email/' : ''}verify`, method === 'email'
        ? { email, otp: code } : { mobile, otp: code, ...scope }, { withCredentials: true });
      if (data.status === 'linked') { onLinked?.(); return; }
      if (data.status === 'authenticated') { window.location.assign(authDestination[data.account_type]); return; }
      if (method === 'email') { setError('Unable to sign in. Please use your registered email or email and password.'); return; }
      setProof(data);
    } catch (err) { setError(typeof err.response?.data?.detail === 'string' ? err.response.data.detail : 'Unable to verify this code.'); }
    finally { setBusy(false); }
  };
  if (proof) return <div className="space-y-4">
    <h2 className="text-xl font-bold">Choose your account type</h2>
    <p>Your code is verified. Complete your profile within 10 minutes.</p>
    {['artist', 'collaborator', 'customer'].map(role => <button key={role} className="btn-outline block w-full capitalize" onClick={() => onProof(proof, role)}>{role}</button>)}
  </div>;
  return <div className="space-y-4">
    {!accountType && <div className="flex gap-3">
      <button disabled={busy || Boolean(channel)} type="button" aria-pressed={method === 'email'} onClick={() => { setMethod('email'); setError(''); }}>Email sign-in</button>
      <button disabled={busy || Boolean(channel)} type="button" aria-pressed={method === 'mobile'} onClick={() => { setMethod('mobile'); setError(''); }}>New account / linked mobile</button>
    </div>}
    <p>{accountType ? 'Verify your mobile through WhatsApp. Your existing login remains available.' : method === 'email'
      ? 'Existing artists and collaborators: sign in with your registered email. You can add a mobile number from your account afterwards.'
      : 'Create an account or sign in with an already linked mobile. Existing users with a new number: use email sign-in first.'}</p>
    {error && <p role="alert" className="text-red-700">{error}</p>}
    {method === 'mobile' && <label className="block">Mobile number<input aria-label="Mobile number" className="w-full border rounded-lg p-3" type="tel" autoComplete="tel" value={mobile} disabled={Boolean(channel)} onChange={e => setMobile(e.target.value)} /></label>}
    {!accountType && <label className="block">Email address<input aria-label="Email address" className="w-full border rounded-lg p-3" type="email" autoComplete="email" value={email} disabled={Boolean(channel)} onChange={e => setEmail(e.target.value)} /><span className="text-sm">Existing accounts: use your registered email.</span></label>}
    {channel && <>
      <p role="status">Code sent by {channel}.</p>
      <label className="block">Verification code<input aria-label="Verification code" className="w-full border rounded-lg p-3" inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={code} onChange={e => setCode(e.target.value.replace(/\D/g, ''))} /></label>
      <button className="btn-primary w-full" disabled={busy || code.length !== 6} onClick={verify}>Verify and continue</button>
      <button className="underline" disabled={busy} onClick={() => { setChannel(''); setCode(''); }}>Change details</button>
    </>}
    <OTPChallengeCaptcha key={captchaKey} onVerify={onCaptcha} />
    <button className="btn-outline w-full" disabled={busy || !(method === 'email' ? email : mobile) || cooldown > 0 || (Boolean(process.env.REACT_APP_TURNSTILE_SITE_KEY) && !captcha)} onClick={send}>{cooldown ? `Resend in ${cooldown}s` : channel ? 'Resend code' : 'Send code'}</button>
  </div>;
}
